import PropTypes from "prop-types";
import React from "react";
import cloneDeep from "lodash/cloneDeep";
import lround from "lodash/round";
import fromIntensity from "../../../../utils/get-zone";
import { isNotMac } from "../../../../utils/browser";
import convert from "convert-units";
import { mapParameter } from "../../../../parts/other-exercise/CustomParametersSets";
import {
  calculatePrefixForExercise,
  buildSupersets,
} from "../../../../utils/prefix-calculator";
import submit from "submit-form";
import EditorExerciseLineItem from "./EditorExerciseLineItem";
import NotesWrapper from "../../../dashboard/program-builder/components/NotesWrapper";
import PbExerciseOptions from "./PbExerciseOptions";
import SvgIcon from "../../../../parts/SvgIcon";

class EditorSessionTableRow extends React.Component {
  constructor(props, ctx) {
    super(props, ctx);
    this.state = {
      currentRow: 0,
      isAddingSet: false,
      newMovement: "",
      sets: 0,
      reps: 0,
      rpe: "",
      load: 0,
      percentRepMax: 0,
      addingNote: false,
    };

    // Used to change its value and trigger its onChange from this component
    this.notesInputRef = React.createRef();

    this.localLoadInputs = [];
    this.localIntensityInputs = [];

    this._handleOpenAddSet = this._handleOpenAddSet.bind(this);
    this._handleCloseAddSet = this._handleCloseAddSet.bind(this);
    this._handleNewSetChange = this._handleNewSetChange.bind(this);
    this._handleSaveNewSet = this._handleSaveNewSet.bind(this);
    this._handleNewMovementChange = this._handleNewMovementChange.bind(this);
    this._checkIfEditedExercise = this._checkIfEditedExercise.bind(this);
    this.handleDeleteSetInState = this.handleDeleteSetInState.bind(this);
    this._handleSaveNewSetForOther = this._handleSaveNewSetForOther.bind(this);
    this._handleCreateSuperset = this._handleCreateSuperset.bind(this);
    this._handleRemoveSuperset = this._handleRemoveSuperset.bind(this);
    this._onAddNote = this._onAddNote.bind(this);
  }

  // Add Set functions
  // handles the opening of the Add set line
  _handleOpenAddSet(e, setIndex) {
    e.preventDefault();
    this.setState({ isAddingSet: true, currentRow: setIndex });
  }
  // handles the closing of the Add set line
  // if the add set line is closed, we should reset all the states
  _handleCloseAddSet(e) {
    e.preventDefault();
    this.setState({
      isAddingSet: false,
      currentRow: 0,
      sets: 0,
      reps: 0,
      rpe: "",
      load: 0,
      percentRepMax: 0,
    });
  }
  // handles any changes for the fields in the add set line
  _handleNewSetChange({ target: { name, value } }, defaultRepMax) {
    const { exercises, exercise, fromMetric } = this.props;
    const { units } = this.context.locals.activeUser;
    const activeExercise = exercises.find((elem) => {
      return elem._id === exercise;
    });
    const isAssistant = activeExercise.type === "assistant";
    const { secondaryRepMaximum } = activeExercise;
    const repMax =
      activeExercise.type === "primary"
        ? defaultRepMax
        : Math.round(fromMetric(secondaryRepMaximum, units), 0);

    // if the name is load and an assistant exercise, ensure conversion is alright
    if (name === "load" && isAssistant) {
      // if the units are imperial convert
      if (units === "imperial") {
        this.setState({
          percentRepMax: 0,
          load: lround(convert(value).from("lb").to("kg"), 2),
        });
      }
      // otherwise, simply return the value
      else {
        this.setState({ percentRepMax: 0, load: value });
      }
    }
    // else if the field is load or pRM, calculate and set states.
    else if ((name === "load" || name === "percentRepMax") && !isAssistant) {
      // if name is load
      if (name === "load") {
        this.setState({
          percentRepMax: lround((value / repMax) * 100, 1),
          load: value,
        });
      }
      // else if name is percentRepMax
      else if (name === "percentRepMax") {
        this.setState({
          load: lround((repMax * value) / 100, 2),
          percentRepMax: value,
        });
      }
      // recalculating the fields to be shown.
      const localLoadInput = this.localLoadInputs["new"];
      if (localLoadInput) {
        if (name === "percentRepMax") {
          localLoadInput.value = lround((repMax * value) / 100, 2);
        } else {
          this.localIntensityInputs["new"].value = lround(
            (value / repMax) * 100,
            1
          );
        }
      }
    }
    // else set state dynamically
    else {
      this.setState({ [name]: value });
    }
  }

  validateSaveNewSet(newSet, isAssistant, defaultRepMax) {
    if (
      newSet.sets <= 0 ||
      newSet.sets > 500 ||
      (!isAssistant && defaultRepMax <= 0) ||
      (newSet.rpe !== "AMRAP" && newSet.reps <= 0)
    )
      return false;

    return true;
  }

  _checkIfEditedExercise() {
    return this.props.exercisesInState.findIndex(
      (exercise) => exercise._id === this.props.exercise
    );
  }
  // submits the newly entered set into the exercise
  _handleSaveNewSet(e, isEquipped, defaultRepMax, activeUser, exId, setIndex) {
    e.preventDefault();
    const { sets, reps, rpe, load, percentRepMax } = this.state;
    // Note: activeUser is not the same as the activeUser in the context
    // activeUser is the user or athlete editing or being edited.
    // this.context.locals.activeUser is the user currently Signed on.
    const { units } = this.context.locals.activeUser;
    const isRepRange = reps && reps.includes("-");
    const repsRange = isRepRange ? reps.split("-") : reps;
    const newSet = {
      sets,
      reps: isRepRange ? repsRange[1] || repsRange[0] : reps,
      percentRepMax,
      load:
        units === "imperial"
          ? lround(convert(load).from("lb").to("kg"), 2)
          : load,
      equipped: isEquipped,
      zone: fromIntensity(percentRepMax, reps),
      rpe,
      repsLower: isRepRange && repsRange[1] ? repsRange[0] : null,
    };

    const exerciseIndex = this.props.exercises.findIndex(
      (elem) => elem._id === exId
    );

    const copyExercise = cloneDeep(this.props.exercises[exerciseIndex]);
    const isAssistant = copyExercise.type === "assistant";
    const editedExerciseIndex = this._checkIfEditedExercise();

    if (
      this.props.isProgramEditor &&
      this.validateSaveNewSet(newSet, isAssistant, defaultRepMax)
    ) {
      // optimistic update
      copyExercise.sets.splice(parseInt(setIndex) + 1, 0, newSet);
      this.props.updateExercise(copyExercise);

      if (editedExerciseIndex > -1) {
        // update exercise in state (the one send to the api if clicking on save)
        const copyExerciseEdited = cloneDeep(
          this.props.exercisesInState[editedExerciseIndex]
        );
        copyExerciseEdited.sets.splice(parseInt(setIndex) + 1, 0, newSet);
        this.props.updateExerciseByState(
          copyExerciseEdited,
          editedExerciseIndex
        );
      }
    }

    if (editedExerciseIndex < 0) {
      const activeUserToRequestId = this.context.locals.activeUserToRequest._id;
      const athleteId = this.context.locals.athlete?._id;

      let body = {
        newSet,
        defaultRepMax,
        omitModified: true,
        omitActiveUser: true,
      };

      if (activeUser.organizationId || activeUser?.coach?.organizationId) {
        body = { ...body, organizationCoach: activeUserToRequestId };
      }

      const actionToRequest = athleteId
        ? `/actions/exercise/quick-edit/sets/add/${setIndex}/${exId}/${athleteId}`
        : activeUser?.permissions?.includes("coach")
        ? `/actions/exercise/quick-edit/sets/add/${setIndex}/${exId}/${undefined}`
        : `/actions/exercise/quick-edit/sets/add/${setIndex}/${exId}/${activeUser._id}`;
      submit(actionToRequest, {
        method: "POST",
        body,
      });
    }

    this._handleCloseAddSet(e);
  }

  handleDeleteSetInState(setIndex) {
    const editedExerciseIndex = this._checkIfEditedExercise();
    if (editedExerciseIndex > -1) {
      // update exercise in state (the one send to the api if clicking on save)
      const copyExerciseEdited = cloneDeep(
        this.props.exercisesInState[editedExerciseIndex]
      );
      copyExerciseEdited.sets.splice(setIndex, 1);
      this.props.updateExerciseByState(copyExerciseEdited, editedExerciseIndex);
    }
  }

  _handleNewMovementChange(value, exId, setIndex) {
    const { handleQuickEditChange } = this.props;

    if (value) {
      this.setState({ newMovement: `${value.movementLabel}` });
      const e = {
        target: { name: "exerciseType", value },
      };

      handleQuickEditChange(e, exId, setIndex);
    }
  }
  _handleSaveNewSetForOther(e, exId, setIndex, activeUser, activeExercise) {
    e.preventDefault();

    let newSet = {};
    activeExercise.parameters.forEach((elem) => {
      newSet[mapParameter(elem)] = this.state[mapParameter(elem)];
    });
    const isRepRange = newSet.reps && newSet.reps.includes("-");
    if (newSet.reps) {
      const repsRange = isRepRange ? newSet.reps.split("-") : newSet.reps;
      newSet = {
        ...newSet,
        reps: isRepRange ? repsRange[1] || repsRange[0] : newSet.reps,
        repsLower: isRepRange && repsRange[1] ? repsRange[0] : null,
      };
    }
    const exerciseIndex = this.props.exercises.findIndex(
      (elem) => elem._id === exId
    );
    const copyExercise = cloneDeep(this.props.exercises[exerciseIndex]);
    const editedExerciseIndex = this._checkIfEditedExercise();

    if (this.props.isProgramEditor) {
      // optimistic update
      copyExercise.sets.splice(parseInt(setIndex) + 1, 0, newSet);
      this.props.updateExercise(copyExercise);

      if (editedExerciseIndex > -1) {
        // update exercise in state (the one send to the api if clicking on save)
        const copyExerciseEdited = cloneDeep(
          this.props.exercisesInState[editedExerciseIndex]
        );
        copyExerciseEdited.sets.splice(parseInt(setIndex) + 1, 0, newSet);
        this.props.updateExerciseByState(
          copyExerciseEdited,
          editedExerciseIndex
        );
      }
    }

    if (editedExerciseIndex < 0) {
      const activeUserToRequestId = this.context.locals.activeUserToRequest._id;
      const athleteId = this.context.locals.athlete?._id;

      let body = {
        newSet,
        defaultRepMax: "",
      };
      if (activeUser.organizationId || activeUser?.coach?.organizationId) {
        body = { ...body, organizationCoach: activeUserToRequestId };
      }

      const actionToRequest = athleteId
        ? `/actions/exercise/quick-edit/sets/add/${setIndex}/${exId}/${athleteId}`
        : activeUser?.permissions?.includes("coach")
        ? `/actions/exercise/quick-edit/sets/add/${setIndex}/${exId}/${undefined}`
        : `/actions/exercise/quick-edit/sets/add/${setIndex}/${exId}/${activeUser._id}`;
      submit(actionToRequest, {
        method: "POST",
        body,
      });
    }

    this._handleCloseAddSet(e);
  }

  _handleCreateSuperset(fullExercise, exercise, nextExercise) {
    submit(`/actions/exercise/superset/add`, {
      method: "POST",
      body: {
        exercise,
        nextExercise,
        fullExercise,
      },
    });
    if (typeof this.props.setEditing === "function") {
      this.props.setEditing();
    }
  }

  _handleRemoveSuperset(fullExercise, exercise) {
    submit(`/actions/exercise/superset/remove`, {
      method: "POST",
      body: {
        exercise,
        fullExercise,
      },
    });
    if (typeof this.props.setEditing === "function") {
      this.props.setEditing();
    }
  }

  _onAddNote() {
    this.setState({ addingNote: true });
  }

  render() {
    const { pathname, query, withWeight } = this.context.locals;
    const {
      exercises = [],
      exercise,
      fromMetric,
      activeUser,
      handleDuplicate,
      isProgramEditor,
      openQuickEditMode,
      handleQuickEditChange,
      loadInputs,
      intensityInputs,
      isQuickEditOpen,
      session,
      sessionIndex,
      updateExercise,
      deleteExercise,
      stateExercises,
      isCoach,
    } = this.props;
    const {
      isAddingSet,
      currentRow,
      load,
      sets,
      rpe,
      reps,
      percentRepMax,
      newMovement,
    } = this.state;
    const newSet = {
      sets,
      load,
      rpe,
      reps,
      percentRepMax,
    };

    const activeExercise = exercises.find((ex) => ex._id === exercise);

    // Need the state exercises so we can update the notes state
    const activeStateExercise =
      stateExercises.find((ex) => ex._id === exercise) || activeExercise;
    const supersets = buildSupersets(exercises);
    const activeExerciseIndex = exercises.findIndex(
      (ex) => ex._id == activeExercise._id
    );

    // Have to trim the notes cause in some cases comes as " " which is set
    // like that to avoid rill from getting rid of it, but still counts as an empty note
    const notes = activeStateExercise?.notes;
    const exHasNotes =
      notes === 0 || notes === "0"
        ? false
        : !!Number(notes) || notes?.trim().length > 0;

    if (activeExercise && activeExercise.sets) {
      const isLast = activeExerciseIndex === exercises.length - 1;
      const nextExercise = !isLast && exercises[activeExerciseIndex + 1];
      const isSuperset = activeExercise.nextSuperset === nextExercise._id;
      const isNotMacValue = isNotMac();

      const onInputHandleQuickEditChange = (e) =>
        handleQuickEditChange(e, activeExercise._id, null);

      const onClickOpenQuickEditMode = (e) => openQuickEditMode(e);

      let prefix = "";
      if (activeExerciseIndex >= 0) {
        prefix = calculatePrefixForExercise(activeExercise._id, supersets);
      }

      const OrderArrowsComponent = () => (
        <img
          src="/img/new-icons/drag-outline.svg"
          className="grabber cursor-grab drag-icon"
          alt="drag"
        />
      );

      const OptionsComponent = () => (
        <PbExerciseOptions
          activeExercise={activeExercise}
          week={session.week}
          session={sessionIndex + 1}
        />
      );

      return (
        <li
          className={`Program-Editor-Exercise ${this.props.altClass || ""} ${
            isNotMacValue ? "padding-scroll-no-mac" : ""
          } ${isSuperset ? "is-superset" : ""}`}
          data-id={activeExercise._id}
          id={activeExercise._id}
          key={activeExercise._id}
          onClick={onClickOpenQuickEditMode}
        >
          {activeExercise.sets.map((set, setIndex) => {
            return (
              <EditorExerciseLineItem
                key={`${activeExercise._id}${setIndex}`}
                handleQuickEditChange={handleQuickEditChange}
                _handleOpenAddSet={this._handleOpenAddSet}
                _handleCloseAddSet={this._handleCloseAddSet}
                _handleNewSetChange={this._handleNewSetChange}
                _handleSaveNewSet={this._handleSaveNewSet}
                _handleNewMovementChange={this._handleNewMovementChange}
                set={set}
                newSet={newSet}
                setIndex={setIndex}
                activeExercise={activeExercise}
                isProgramEditor={isProgramEditor}
                fromMetric={fromMetric}
                activeUser={activeUser}
                withWeight={withWeight}
                loadInputs={loadInputs}
                intensityInputs={intensityInputs}
                localLoadInputs={this.localLoadInputs}
                localIntensityInputs={this.localIntensityInputs}
                isQuickEditOpen={isQuickEditOpen}
                isAddingSet={isAddingSet}
                currentRow={currentRow}
                units={this.context.locals.activeUser.units}
                newMovement={newMovement}
                setLength={activeExercise.sets.length}
                activeContextUser={this.context.locals.activeUser}
                activeUserToRequest={this.context.locals.activeUserToRequest}
                queryEditedExercise={this.context.locals.query.editExerciseId}
                updateExercise={updateExercise}
                deleteExercise={deleteExercise}
                handleDeleteSetInState={this.handleDeleteSetInState}
                _handleSaveNewSetForOther={this._handleSaveNewSetForOther}
                OrderArrowsComponent={OrderArrowsComponent}
                prefix={prefix}
                OptionsComponent={OptionsComponent}
                isCoach={isCoach}
                activeUserToHandleCopyAction={
                  this.context.locals.athlete ||
                  this.context.locals.activeUserToRequest
                }
              />
            );
          })}
          {(exHasNotes || this.state.addingNote) && (
            <NotesWrapper
              isProgramEditor={isProgramEditor}
              openQuickEditMode={isQuickEditOpen}
              onInputHandleQuickEditChange={onInputHandleQuickEditChange}
              activeExercise={activeExercise}
              notesInputRef={this.notesInputRef}
            />
          )}

          <div role="supersets-notes-buttons">
            {isQuickEditOpen && nextExercise && !isLast && !isSuperset && (
              <button
                data-button-type="text"
                className="superset-button"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  this._handleCreateSuperset(
                    activeExercise,
                    activeExercise._id,
                    nextExercise._id
                  );
                }}
              >
                <img
                  src="/img/new-icons/superset-gradient.svg"
                  height={16}
                  width={16}
                />
                <span>Superset</span>
              </button>
            )}

            {isQuickEditOpen && nextExercise && !isLast && isSuperset && (
              <button
                data-button-type="text"
                className="superset-button"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  this._handleRemoveSuperset(
                    activeExercise,
                    activeExercise._id,
                    nextExercise._id
                  );
                }}
              >
                <img
                  src="/img/new-icons/superset-danger.svg"
                  height={16}
                  width={16}
                />
                <span style={{ background: "none", color: "var(--danger)" }}>
                  Remove Superset
                </span>
              </button>
            )}

            {!exHasNotes && isQuickEditOpen && !this.state.addingNote && (
              <button
                data-button-type="text"
                onClick={() => {
                  this._onAddNote();
                }}
              >
                <SvgIcon
                  src="/img/new-icons/note-gradient.svg"
                  alt="add notes"
                />
                <span>Add Note</span>
              </button>
            )}

            {exHasNotes && isQuickEditOpen && (
              <button
                data-button-type="text-white"
                data-action="cancel"
                onClick={() => {
                  // First closing the adding note state
                  this.setState({ addingNote: false });

                  // then if we have the inputRef, change its value
                  if (this.notesInputRef?.current) {
                    this.notesInputRef.current.value = "";

                    // And then simulate the input event so the onChange gets triggered
                    const event = new Event("input", { bubbles: true });
                    this.notesInputRef.current.dispatchEvent(event);
                  }
                }}
              >
                <img
                  src={"/img/new-icons/red-trash.svg"}
                  width={16}
                  height={16}
                />
                <span>Remove Note</span>
              </button>
            )}
          </div>
        </li>
      );
    }
    return null;
  }
}

EditorSessionTableRow.contextTypes = { locals: PropTypes.object };

export default EditorSessionTableRow;
